/**
 * Gemeinsame Datenquellen + Helfer der öffentlichen Seiten (Desktop und Mobil).
 * Damit bleiben Bildzuordnung, Endpunkte und Formatierung an einer Stelle.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useEditState } from './editStore';


export const HERO_PHOTOS = [
  '/kac-assets/photos/DSC00123.jpg',
  '/kac-assets/photos/DSC00933.jpg',
  '/kac-assets/photos/DSC00317.jpg',
  '/kac-assets/photos/DSC09748.jpg',
];

const EVENT_IMAGE_MAP = [
  { keywords: ['leiter', 'leader'], file: 'slideLead.png' },
  { keywords: ['hub'], file: 'slideHub.png' },
  { keywords: ['morning prayer'], file: 'slideMorning.jpg' },
  { keywords: ['evening prayer'], file: 'slideEvening.jpg' },
  { keywords: ['kingdom school'], file: 'slideKingdomSchool.jpg' },
  { keywords: ['kingdom connect'], file: 'slideConnect.jpg' },
  { keywords: ['sunday service'], file: 'slideSundayService.jpg' },
  { keywords: ['foundation'], file: 'slideFoundation.jpg' },
  { keywords: ['q&a-session'], file: 'slideQA.jpg' },
  { keywords: ['workers meeting'], file: 'slideWorker.jpg' },
  { keywords: ['integration day', 'integration class'], file: 'slideIntegration.jpg' },
  { keywords: ['charlottenburg'], file: 'slideCP.jpg' },
];

export const eventImage = title => {
  const s = (title || '').toLowerCase();
  const match = EVENT_IMAGE_MAP.find(m => m.keywords.some(k => s.includes(k)));
  return '/kac-assets/events/' + (match ? match.file : 'slideTermine.png');
};

export const eventDate = e => e?.start || e?.start_time || e?.date || '';

const fmt = (d, opts, locale = 'de-DE') => {
  try { return new Intl.DateTimeFormat(locale, opts).format(new Date(d)); } catch { return ''; }
};
/** Seitensprache Englisch? (feste Texte stehen seit W4 als Kennungen in den Uebersetzungen, kein pick() mehr) */
export const isEnglish = lang => String(lang || '').startsWith('en');
/** Uhrzeit wie auf Deutsch „16:30 Uhr“, auf Englisch „16:30“. */
export const clock = (lang, hhmm) => (hhmm ? (isEnglish(lang) ? hhmm : `${hhmm} Uhr`) : '');
/** Zitat mit passenden Anfuehrungszeichen; vorhandene Anfuehrungszeichen im gepflegten Text fallen weg. */
export const quote = (lang, text) => {
  const inner = String(text || '').trim().replace(/^["\u201e\u201c\u201d\u00ab\u00bb']+|["\u201e\u201c\u201d\u00ab\u00bb']+$/g, '').trim();
  if (!inner) return '';
  return isEnglish(lang) ? `\u201c${inner}\u201d` : `\u201e${inner}\u201c`;
};

export const fmtDay = (d, l) => fmt(d, { weekday: 'long' }, l);
export const fmtDate = (d, l) => fmt(d, { day: 'numeric', month: 'long' }, l);
export const fmtTime = (d, l) => fmt(d, { hour: '2-digit', minute: '2-digit' }, l);

/**
 * Termine und Kontakt kommen seit 10/2026 aus der oeffentlichen AgoraHub-API (/public/v1).
 * Gepflegt wird in AgoraHub; ausgeliefert werden nur freigegebene, oeffentliche Inhalte.
 */
export const AGORA_PUBLIC_BASE =
  'https://api.agorahub.de/public/v1/tenants/kingdom-ambassador-centre-berlin';

let campusSlugPromise = null;
/** Campus-Kurzname aus dem Manifest (einmal je Seitenaufruf). */
export function agoraCampusSlug() {
  if (!campusSlugPromise) {
    campusSlugPromise = axios
      .get(`${AGORA_PUBLIC_BASE}/manifest`)
      .then(res => res.data?.campuses?.[0]?.slug || '')
      .catch(() => { campusSlugPromise = null; return ''; });
  }
  return campusSlugPromise;
}

/**
 * Gebetsanliegen an AgoraHub. Die Beter der Gemeinde sehen es in der App; Gemeinde und Campus
 * bestimmt der Server, nicht das Formular.
 */
export async function submitPrayerRequest({ name, request, contactRequested, phoneNumber }) {
  const campus = await agoraCampusSlug();
  await axios.post(`${AGORA_PUBLIC_BASE}/prayer-requests`, {
    name: name || '',
    request,
    contactRequested: Boolean(contactRequested),
    phoneNumber: contactRequested ? (phoneNumber || '') : '',
    ...(campus ? { campus } : {}),
  });
}

/** Kommende oeffentliche Termine aus AgoraHub, aufsteigend. */
export async function fetchUpcomingEvents(limit = 10) {
  const now = new Date();
  const to = new Date(now.getTime() + 90 * 24 * 3600 * 1000);
  const collected = [];
  let cursor = null;
  // Die API filtert nach dem Lesen; mehrere Seiten sammeln, bis genug Termine da sind.
  for (let page = 0; page < 5 && collected.length < limit; page += 1) {
    const params = { from: now.toISOString(), to: to.toISOString(), limit: 100 };
    if (cursor) params.cursor = cursor;
    const res = await axios.get(`${AGORA_PUBLIC_BASE}/events`, { params });
    collected.push(...(res.data?.events || []));
    cursor = res.data?.next_cursor;
    if (!cursor) break;
  }
  return collected
    .filter(e => eventDate(e) && new Date(eventDate(e)) >= now)
    .sort((a, b) => new Date(eventDate(a)) - new Date(eventDate(b)))
    .slice(0, limit);
}

export function useUpcomingEvents(limit = 10) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchUpcomingEvents(limit).then(list => { if (alive) setEvents(list); }).catch(() => {});
    return () => { alive = false; };
  }, [limit]);

  return events;
}

/**
 * Wandelt das AgoraHub-Kontaktverzeichnis in die Form, die die Seiten schon kennen
 * (locations[].active/addressLine1, service_times.<tag>.time, contact_details.email.address).
 */
export function toLegacyContactInfo(content) {
  if (!content) return null;
  const serviceTimes = {};
  (content.service_times || []).forEach(t => { serviceTimes[t.key] = { ...t, active: true }; });
  const details = {};
  Object.entries(content.contact_details || {}).forEach(([k, v]) => { details[k] = { ...v, active: true }; });
  return {
    locations: (content.locations || []).map(l => ({
      ...l,
      active: true,
      displayName: l.display_name,
      addressLine1: l.address_line1,
      addressLine2: l.address_line2,
    })),
    service_times: serviceTimes,
    contact_details: details,
    social_media: (content.social_media || []).map(s => ({ ...s, active: true })),
  };
}

export async function fetchContactInfo() {
  const campus = await agoraCampusSlug();
  const res = await axios.get(`${AGORA_PUBLIC_BASE}/content/contact_info`, {
    params: campus ? { campus } : {},
  });
  return toLegacyContactInfo(res.data);
}

/**
 * Standort, Gottesdienstzeiten und Kontaktdaten — immer live aus AgoraHub,
 * damit z. B. ein Adresswechsel nicht in Seiten hartkodiert nachgezogen werden muss.
 */
export function useContactInfo() {
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchContactInfo().then(info => { if (alive) setContactInfo(info); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const location = contactInfo?.locations?.find(l => l.active) || null;
  const times = contactInfo?.service_times || null;
  const email = contactInfo?.contact_details?.email?.address || 'welcome@kacberlin.com';

  const mapsQuery = location
    ? encodeURIComponent(`${location.addressLine1}, ${location.addressLine2}`)
    : '';
  const mapsUrl = mapsQuery ? `https://www.google.com/maps/search/?api=1&query=${mapsQuery}` : '#';
  const mapsEmbedUrl = mapsQuery
    ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${mapsQuery}`
    : null;

  return { contactInfo, location, times, email, mapsUrl, mapsEmbedUrl };
}


/* ---------- Website-Bilder und Spendendaten aus AgoraHub ----------
 * Website-Texte und Seitentitel kommen seit W4 (19.10.2026) aus `editTexts.js` (Kennungen = Uebersetzungs-Schluessel).
 * Bilder: Plaetze aus AgoraHub (Inhalte → Gemeinde-Website), im Bearbeitungsmodus ueberlagert vom Entwurf.
 */
/** Mehrzeilige Angabe (Impressum): eine Zeile je Eintrag. */
export const textLines = v => String(v || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
/** Absaetze: Liste oder Text mit Leerzeilen. */
export const textParagraphs = v => (Array.isArray(v) ? v : String(v || '').split(/\n\s*\n/)).map(s => String(s || '').trim()).filter(Boolean);

export const isHttps = u => typeof u === 'string' && u.startsWith('https://');
const pct = n => { const v = Number(n); return `${Math.round((Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5) * 1000) / 10}%`; };
export const focusOf = im => `${pct(im?.focus_x)} ${pct(im?.focus_y)}`;
/** Bilder eines Platzes (nur https), leer wenn nichts gepflegt. */
export function slotList(slots, id) {
  const list = slots && slots[id];
  return Array.isArray(list) ? list.filter(im => im && isHttps(im.url)) : [];
}
/** Hintergrund-Stil fuer einen Platz: AgoraHub-Bild mit Fokuspunkt, sonst das eingebaute Bild. */
export function slotBackground(slots, id, index, fallbackUrl, fallbackPosition = 'center') {
  const im = slotList(slots, id)[index || 0];
  return im
    ? { backgroundImage: `url("${im.url}")`, backgroundPosition: focusOf(im) }
    : { backgroundImage: `url("${fallbackUrl}")`, backgroundPosition: fallbackPosition };
}
/** Bild-URL eines Platzes, sonst das eingebaute Bild. */
export const slotUrl = (slots, id, index, fallbackUrl) => (slotList(slots, id)[index || 0] || {}).url || fallbackUrl;
/** Startseiten-Slideshow: Platz home.hero, sonst die eingebauten Fotos. */
export function heroPhotos(slots) {
  const list = slotList(slots, 'home.hero').map(im => im.url);
  return list.length ? list : HERO_PHOTOS;
}
/** Terminbild: Bild am Termin, sonst Platz service.<art>, sonst Stichwort-Zuordnung. */
export function eventImageFor(event, slots) {
  if (isHttps(event?.image_url)) return event.image_url;
  const type = String(event?.event_type || '').toLowerCase();
  const im = (type && slotList(slots, `service.${type}`)[0]) || slotList(slots, 'service.event')[0];
  return im ? im.url : eventImage(event?.title || event?.summary);
}
/** Terminarten mit eigenem Bild-Platz (AgoraHub agora_website_slots.EVENT_KINDS). */
const EVENT_KINDS = ['service', 'prayer', 'community', 'meeting', 'event'];
/** Bild-Platz eines Termins ohne eigenes Bild (Terminart aus AgoraHub, sonst allgemein) fuer den Bearbeitungsmodus. */
export function eventSlot(event) {
  if (!event || isHttps(event.image_url)) return undefined;
  const kind = String(event.event_type || '').toLowerCase();
  return `service.${EVENT_KINDS.includes(kind) ? kind : 'event'}`;
}
let imagesPromise = null;
export function fetchWebsiteImages() {
  if (!imagesPromise) {
    imagesPromise = agoraCampusSlug()
      .then(campus => axios.get(`${AGORA_PUBLIC_BASE}/content/website`, { params: campus ? { campus } : {} }))
      .then(res => (res.data && res.data.slots && typeof res.data.slots === 'object' ? res.data.slots : {}))
      .catch(() => ({}));
  }
  return imagesPromise;
}
export function useWebsiteImages() {
  const [slots, setSlots] = useState(null);
  const drafts = useEditState(state => state.slots);   // nur im Bearbeitungsmodus gefuellt (setSlot)
  useEffect(() => {
    let alive = true;
    fetchWebsiteImages().then(s => { if (alive) setSlots(s); });
    return () => { alive = false; };
  }, []);
  return useMemo(() => (Object.keys(drafts).length ? { ...(slots || {}), ...drafts } : slots), [slots, drafts]);
}

/**
 * Bankdaten und PayPal aus AgoraHub (Inhalte → Geben). Bewusst NICHT im Website-Code und nicht ueber den
 * Bearbeitungsmodus aenderbar: wer nur die Website bearbeiten darf, soll keine Spenden umleiten koennen.
 */
let givingPromise = null;
export function fetchGiving() {
  if (!givingPromise) {
    givingPromise = agoraCampusSlug()
      .then(campus => axios.get(`${AGORA_PUBLIC_BASE}/content/giving`, { params: campus ? { campus } : {} }))
      .then(res => (res.data && res.data.locales && typeof res.data.locales === 'object' ? res.data.locales : {}))
      .catch(() => { givingPromise = null; return {}; });
  }
  return givingPromise;
}
/** Zeilen der Bankverbindung: Bezeichnung (Kennung giving.bank.<key>) + Feld in AgoraHub (bank_details.<field>). */
export const BANK_ROWS = [
  { key: 'inhaber', field: 'account_holder' },
  { key: 'iban', field: 'iban' },
  { key: 'bic', field: 'bic' },
  { key: 'zweck', field: 'reference' },
];
/** Spendendaten in der Seitensprache, je Feld Rueckfall auf Deutsch. `ready` = Antwort da (auch wenn leer). */
export function givingFor(locales, lang) {
  const de = (locales && locales.de) || {};
  const own = (locales && locales[isEnglish(lang) ? 'en' : 'de']) || {};
  const bank = {};
  BANK_ROWS.forEach(({ field }) => {
    const value = (own.bank_details || {})[field] || (de.bank_details || {})[field];
    if (typeof value === 'string' && value.trim()) bank[field] = value.trim();
  });
  const paypalUrl = [own.paypal_url, de.paypal_url].find(isHttps) || '';
  return { bank, paypalUrl };
}
export function useGiving(lang) {
  const [locales, setLocales] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchGiving().then(l => { if (alive) setLocales(l); });
    return () => { alive = false; };
  }, []);
  return { ready: locales !== null, ...givingFor(locales, lang) };
}
